import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiBaseUrl, logApiConfig } from '../utils/apiConfig';

// MathVenture Login Component - Clean Version

const RejectionNotificationModal = ({ rejectedPayments, onClose }) => {
  if (!rejectedPayments || rejectedPayments.length === 0) return null;

  const latestRejection = rejectedPayments[0];

  return (
    <div className="fixed inset-0 bg-purple-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border-4 border-orange-400 w-96 shadow-2xl rounded-3xl bg-gradient-to-br from-yellow-100 to-orange-100">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full bg-red-200 border-3 border-red-400">
                <span className="text-3xl">😔</span>
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-bold text-red-700">Oops! Payment Issue</h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 bg-white rounded-full p-2 border-2 border-gray-300 hover:border-gray-400 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="mb-6">
            <div className="bg-red-100 border-3 border-red-300 rounded-2xl p-4 mb-4">
              <h4 className="font-bold text-red-800 mb-3 text-lg flex items-center">
                <span className="mr-2 text-xl">❌</span>
                Your {latestRejection.plan_name} request needs attention
              </h4>
              <p className="text-red-700 text-sm mb-2 flex items-center">
                <span className="mr-2">💰</span>
                <strong>Amount:</strong> NRs. {latestRejection.amount}
              </p>
              <p className="text-red-700 text-sm flex items-center">
                <span className="mr-2">📅</span>
                <strong>Date:</strong> {new Date(latestRejection.created_at).toLocaleDateString()}
              </p>
            </div>
            
            <div className="bg-blue-100 border-3 border-blue-300 rounded-2xl p-4 mb-4">
              <h5 className="font-bold text-blue-800 mb-3 flex items-center">
                <span className="mr-2 text-xl">💬</span>
                Why it didn't work:
              </h5>
              <p className="text-blue-700 text-sm bg-white rounded-xl p-3 border-2 border-blue-200">
                {latestRejection.rejection_reason || 'No specific reason provided'}
              </p>
            </div>
          </div>

          <div className="bg-green-100 border-3 border-green-300 rounded-2xl p-4 mb-6">
            <h5 className="font-bold text-green-800 mb-3 flex items-center">
              <span className="mr-2 text-xl">🚀</span>
              What to do next:
            </h5>
            <ul className="text-green-700 text-sm space-y-2">
              <li className="flex items-center">
                <span className="mr-2">🔍</span>
                Check the reason above
              </li>
              <li className="flex items-center">
                <span className="mr-2">📞</span>
                Ask a grown-up for help if needed
              </li>
              <li className="flex items-center">
                <span className="mr-2">🔄</span>
                Try submitting a new payment
              </li>
              <li className="flex items-center">
                <span className="mr-2">📋</span>
                Make sure all information is correct
              </li>
            </ul>
          </div>

          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-3 text-lg font-bold text-white bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center"
            >
              <span className="mr-2">👍</span>
              Got it!
              <span className="ml-2">✨</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Login = ({ setUser }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectedPayments, setRejectedPayments] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Log API configuration for debugging
    const API_BASE = logApiConfig();
    console.log('🔗 Using API Base URL:', API_BASE);
    console.log('🌍 Full login URL:', `${API_BASE}/api/auth/login`);

    try {
      console.log('🔐 Attempting login with:', { username: formData.username, password: '[HIDDEN]' });
      
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('📡 Login response status:', response.status);
      console.log('📡 Login response headers:', Object.fromEntries(response.headers.entries()));

      if (response.status === 404) {
        throw new Error(`Login endpoint not found. Check if backend is running on ${API_BASE}`);
      }

      if (response.status === 500) {
        const errorText = await response.text();
        console.error('🚨 Server error response:', errorText);
        throw new Error(`Server error (500). Backend may have crashed. Response: ${errorText.substring(0, 200)}`);
      }

      if (response.ok) {
        const data = await response.json();
        console.log('Login successful, full response:', data);
        
        const userData = {
          ...data.user,
          token: data.token
        };
        
        console.log('User data after processing:', userData);
        console.log('Is admin?:', userData.is_admin);
        console.log('Role:', userData.role);
        
        setUser(userData);
        
        // Store user data in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Check for rejected payments and show notification
        try {
          console.log('Checking payment history for user:', userData.id);
          const paymentsResponse = await fetch(`${API_BASE}/api/user/${userData.id}/payments`);
          if (paymentsResponse.ok) {
            const payments = await paymentsResponse.json();
            console.log('User payments:', payments);
            const rejectedPayments = payments.filter(p => p.status === 'rejected');
            console.log('Rejected payments found:', rejectedPayments.length);
            
            // Only show rejection modal if user doesn't have active subscription
            // If user is Premium (role 2+) or has active subscription, don't show old rejections
            const hasActiveSubscription = userData.subscription && userData.subscription.status === 1;
            const isPremiumUser = userData.role >= 2;
            const shouldShowRejections = rejectedPayments.length > 0 && !hasActiveSubscription && !isPremiumUser;
            
            console.log('Rejection modal decision:', {
              hasRejectedPayments: rejectedPayments.length > 0,
              hasActiveSubscription,
              isPremiumUser,
              userRole: userData.role,
              shouldShowRejections
            });
            
            if (shouldShowRejections) {
              // Show rejection notification modal only if user doesn't have active access
              setRejectedPayments(rejectedPayments);
              setShowRejectionModal(true);
              console.log('Showing rejection modal for payments:', rejectedPayments);
              return; // Don't navigate yet, let user close modal first
            } else if (rejectedPayments.length > 0) {
              console.log('User has rejected payments but also has active subscription/premium role - not showing modal');
            }
          }
        } catch (error) {
          console.log('Could not check payment status:', error);
        }
        
        // Redirect based on user role (only if no rejection modal is shown)
        if (!showRejectionModal) {
          if (userData.role === 'admin' || userData.is_admin) {
            console.log('Admin user detected, redirecting to dashboard-selection');
            navigate('/dashboard-selection');
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        const errorData = await response.json();
        console.error('Login failed with status:', response.status, 'Error:', errorData);
        setError(errorData.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(`Network error: ${error.message}. Make sure the backend server is running on ${API_BASE}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectionModalClose = () => {
    console.log('Closing rejection modal...');
    setShowRejectionModal(false);
    setRejectedPayments([]);
    
    // Navigate after modal is closed
    const userData = JSON.parse(localStorage.getItem('user'));
    console.log('Navigating after modal close, user data:', userData);
    if (userData) {
      if (userData.role === 'admin' || userData.is_admin) {
        console.log('Navigating admin to dashboard-selection');
        navigate('/dashboard-selection');
      } else {
        console.log('Navigating user to dashboard');
        navigate('/dashboard');
      }
    } else {
      console.log('No user data found in localStorage for navigation');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-purple-200 to-blue-200 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Fun floating shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-12 h-12 bg-yellow-300 rounded-full opacity-70 animate-bounce"></div>
        <div className="absolute top-20 right-20 w-8 h-8 bg-green-300 rounded-full opacity-70 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-14 h-14 bg-orange-300 rounded-full opacity-70 animate-bounce" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-10 right-10 w-10 h-10 bg-red-300 rounded-full opacity-70 animate-pulse" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-1/2 left-5 w-6 h-6 bg-blue-300 rounded-full opacity-70 animate-bounce" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/3 right-5 w-8 h-8 bg-purple-300 rounded-full opacity-70 animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="max-w-sm w-full space-y-4 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 border-3 border-yellow-300 transform hover:scale-105 transition-transform duration-300">
          <div className="text-center">
            <div className="mx-auto flex justify-center mb-4 relative">
              <div className="relative">
                <img 
                  src="/images/logo/MathVenture_logo.png" 
                  alt="MathVenture Logo" 
                  className="h-16 w-auto animate-pulse"
                />
                <div className="absolute -top-1 -right-1 text-lg animate-bounce">✨</div>
              </div>
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-2">
              🎉 Welcome Back! 🎉
            </h2>
            <p className="text-sm text-purple-700 font-medium mb-2">
              Ready for more math adventures?
            </p>
            <p className="text-xs text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-bold text-pink-600 hover:text-pink-700 underline decoration-wavy">
                Join the fun!
              </Link>
            </p>
          </div>
          
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div className="relative">
                <label htmlFor="username" className="block text-xs font-bold text-purple-700 mb-1">
                  📧 Your Email Address
                </label>
                <input
                  id="username"
                  name="username"
                  type="email"
                  required
                  className="w-full px-3 py-2 border-2 border-purple-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-500 transition-all duration-300 hover:border-purple-400 bg-purple-50"
                  placeholder="your.email@example.com"
                  value={formData.username}
                  onChange={handleChange}
                />
                <div className="absolute right-2 top-6 text-sm">🎯</div>
              </div>
              
              <div className="relative">
                <label htmlFor="password" className="block text-xs font-bold text-purple-700 mb-1">
                  🔐 Secret Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full px-3 py-2 pr-10 border-2 border-purple-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-500 transition-all duration-300 hover:border-purple-400 bg-purple-50"
                  placeholder="Your super secret password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-6 text-purple-600 hover:text-purple-800 focus:outline-none"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
                <div className="absolute right-2 top-6 text-sm"></div>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-2 rounded-xl text-xs font-medium flex items-center">
                <span className="mr-2 text-sm">⚠️</span>
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border-0 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="mr-2 text-sm">🚀</span>
                {loading ? 'Logging in... ⏳' : 'Let\'s Go! 🎮'}
                <span className="ml-2 text-sm">✨</span>
              </button>
            </div>

            <div className="mt-4">
              <Link
                to="/guest"
                className="w-full inline-flex justify-center py-2 px-3 border-2 border-orange-300 rounded-xl shadow-lg bg-gradient-to-r from-orange-200 to-yellow-200 text-sm font-bold text-orange-700 hover:from-orange-300 hover:to-yellow-300 transform hover:scale-105 transition-all duration-300"
              >
                <span className="mr-2 text-sm">👋</span>
                Try as Guest First! 
                <span className="ml-2 text-sm">🎪</span>
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Rejection Notification Modal */}
      {showRejectionModal && (
        <RejectionNotificationModal 
          rejectedPayments={rejectedPayments}
          onClose={handleRejectionModalClose}
        />
      )}
    </div>
  );
};

export default Login;